import type { Chat, ChatRole, MemberPermissions } from '@andresot/protocol';
import type { ChatUpdate, InviteCreate } from '@andresot/protocol/chat-management';
import { request } from './client.js';

function chatPath(chatId: string): string {
	return `/api/chats/${encodeURIComponent(chatId)}`;
}

export async function addMembers(chatId: string, userIds: string[]): Promise<Chat> {
	const response = await request<{ chat: Chat }>(`${chatPath(chatId)}/members`, {
		body: { userIds }
	});
	return response.chat;
}

export async function updateMember(
	chatId: string,
	userId: string,
	input: { role?: Exclude<ChatRole, 'owner'>; restrictedForMinutes?: number | null }
): Promise<Chat> {
	const response = await request<{ chat: Chat }>(
		`${chatPath(chatId)}/members/${encodeURIComponent(userId)}`,
		{ method: 'PATCH', body: input }
	);
	return response.chat;
}

export async function removeMember(chatId: string, userId: string, ban: boolean): Promise<Chat> {
	const response = await request<{ chat: Chat }>(
		`${chatPath(chatId)}/members/${encodeURIComponent(userId)}?ban=${ban}`,
		{ method: 'DELETE' }
	);
	return response.chat;
}

export async function unbanMember(chatId: string, userId: string): Promise<Chat> {
	const response = await request<{ chat: Chat }>(
		`${chatPath(chatId)}/bans/${encodeURIComponent(userId)}`,
		{ method: 'DELETE' }
	);
	return response.chat;
}

export async function updatePermissions(
	chatId: string,
	input: Partial<MemberPermissions>
): Promise<Chat> {
	const response = await request<{ chat: Chat }>(`${chatPath(chatId)}/permissions`, {
		method: 'PATCH',
		body: input
	});
	return response.chat;
}

/** Правка самой группы: название, описание, аватар, реакции. */
export async function updateChat(chatId: string, input: ChatUpdate): Promise<Chat> {
	const response = await request<{ chat: Chat }>(chatPath(chatId), {
		method: 'PATCH',
		body: input
	});
	return response.chat;
}

/**
 * Передать права владельца. Уходящему чат не возвращается — он больше
 * не участник, поэтому ответ здесь и различается.
 */
export async function transferOwner(
	chatId: string,
	input: { targetUserId: string; leaveAfterTransfer: boolean }
): Promise<Chat | null> {
	const response = await request<{ chat?: Chat; left?: boolean }>(
		`${chatPath(chatId)}/transfer-owner`,
		{ body: input }
	);
	return response.chat ?? null;
}

export async function createInvite(
	chatId: string,
	input: InviteCreate = { expiresInHours: 24 * 7, maxUses: null }
): Promise<{ token: string; expiresAt: number }> {
	return request(`${chatPath(chatId)}/invites`, { body: input });
}

export async function revokeInvites(chatId: string): Promise<void> {
	await request(`${chatPath(chatId)}/invites`, { method: 'DELETE' });
}

export type JoinChatResult =
	{ status: 'joined'; chat: Chat } | { status: 'pending'; chatId: string };

export async function joinChat(token: string): Promise<JoinChatResult> {
	return request<JoinChatResult>('/api/chats/join', { body: { token } });
}

export async function leaveChat(chatId: string): Promise<void> {
	await request(`${chatPath(chatId)}/leave`, { body: {} });
}
